import { Link } from "react-router";
import { useLingui } from "@lingui/react";
import { Badge } from "@/components/ui/badge";
import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import { FormulaTip } from "@/components/ui/formula-tip";

export type AttentionSeverity = "urgent" | "warning" | "info";

export interface AttentionItemProps {
  icon: React.ReactNode;
  label: React.ReactNode;
  /** The count badge; omitted for a row whose label already carries the figure (a capacity item). */
  count?: number;
  severity: AttentionSeverity;
  href: string;
  testId?: string;
  /** How the count is calculated: an info icon beside the row (not inside the link). */
  info?: string;
}

const ATTENTION_ICON_CLASS: Record<AttentionSeverity, string> = {
  urgent: "text-destructive",
  warning: "text-warning-on-surface",
  info: "text-info-on-surface",
};

const ATTENTION_DOT_CLASS: Record<AttentionSeverity, string> = {
  urgent: "bg-destructive",
  warning: "bg-warning",
  info: "bg-info",
};

/** One row of the Dashboard's "Attention Required" card: a link to the cases it counts. */
export function AttentionItem({ icon, label, count, severity, href, testId, info }: AttentionItemProps) {
  const { i18n } = useLingui();
  const row = (
    <Link
      to={href}
      data-testid={testId}
      className="flex items-center gap-3 w-full rounded-md px-3 py-2.5 text-left text-sm hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className={cn("h-2 w-2 rounded-full flex-shrink-0", ATTENTION_DOT_CLASS[severity])} aria-hidden="true" />
      <span className={cn("flex-shrink-0", ATTENTION_ICON_CLASS[severity])} aria-hidden="true">
        {icon}
      </span>
      <span className="flex-1 text-foreground">{label}</span>
      {count !== undefined ? (
        <Badge variant="secondary" className="ml-auto tabular-nums">
          {formatNumber(count, i18n.locale)}
        </Badge>
      ) : null}
    </Link>
  );
  if (!info) return row;
  return (
    <div className="flex items-center gap-1">
      <div className="min-w-0 flex-1">{row}</div>
      <FormulaTip content={info} />
    </div>
  );
}
